import { Breadcrumbs, Button, Snackbar } from '@nrx/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFileUpload } from '../hooks/use-file-upload.js';
import type { FxFile, FxFilesClient, Segment } from '../lib/client.js';
import { landingPath, listFolder, uploadFolder, type FolderPath } from '../lib/folders.js';

import { FileTable } from './FileTable.js';
import { UploadDropzone } from './UploadDropzone.js';

export interface FileExchangeViewProps {
  readonly client: FxFilesClient;
  readonly openUrl?: (url: string) => void;
  /** Segments the user may upload into. Default ['inbound'] (partner behavior). */
  readonly uploadableSegments?: readonly Segment[];
}

export function FileExchangeView({
  client,
  openUrl,
  uploadableSegments = ['inbound'],
}: FileExchangeViewProps): React.JSX.Element {
  const [segment, setSegment] = useState<Segment>('inbound');
  const [path, setPath] = useState<FolderPath>([]);
  const [files, setFiles] = useState<FxFile[]>([]);
  const [allowed, setAllowed] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Where the last upload landed, when that is not the level on screen. */
  const [landed, setLanded] = useState<Landing | null>(null);
  // An upload finishes after the render that started it; compare its landing with what is on
  // screen then, not when it was dropped.
  const onScreen = useRef<Landing>({ segment, path });
  useEffect(() => {
    onScreen.current = { segment, path };
  }, [segment, path]);
  const listingRef = useRef<HTMLDivElement>(null);
  const focusListing = useRef(false);
  // Opening a folder or a crumb unmounts the button that was clicked, so focus moves to the
  // listing that replaced it rather than dropping to <body>.
  const navigate = useCallback((next: FolderPath) => {
    focusListing.current = true;
    setLanded(null);
    setPath(next);
  }, []);
  useEffect(() => {
    if (!focusListing.current) return;
    focusListing.current = false;
    listingRef.current?.focus();
  }, [path]);
  const open =
    openUrl ??
    ((url: string) => {
      // The presigned GET carries Content-Disposition: attachment, so an anchor click
      // downloads in place. Using window.open('_blank') instead flashed a tab that the
      // download immediately auto-closed. No target → no tab.
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listed = await client.list(segment);
      setFiles(listed.files);
      setAllowed(listed.folders);
    } catch {
      setFiles([]);
      setAllowed([]);
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [client, segment]);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const listing = useMemo(() => listFolder(files, path, allowed), [files, path, allowed]);
  const crumbs = useMemo(() => breadcrumbTrail(segment, path), [segment, path]);

  const upload = useFileUpload({
    client,
    segment,
    folder: uploadFolder(path),
    onComplete: (keys) => {
      // `segment` is the one uploaded to; `onScreen` is where the user is now.
      const now = onScreen.current;
      // This `refresh` lists the segment uploaded to. After a switch, the switch already
      // re-listed the segment on screen, and re-listing the other would replace it.
      if (segment === now.segment) void refresh();
      const elsewhere = keys
        .map(landingPath)
        .find((p) => segment !== now.segment || p.join('/') !== now.path.join('/'));
      setLanded(elsewhere ? { segment, path: elsewhere } : null);
    },
  });
  const onDownload = useCallback(
    async (key: string) => {
      open(await client.presignDownload(segment, key));
    },
    [client, segment, open],
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(['inbound', 'outbound'] as const).map((s) => (
          <Button
            key={s}
            tone={segment === s ? 'primary' : 'secondary'}
            onClick={() => {
              setSegment(s);
              setLanded(null);
              setPath([]);
            }}
            className="capitalize"
          >
            {s}
          </Button>
        ))}
        <Button tone="secondary" onClick={() => void refresh()} disabled={loading}>
          Refresh
        </Button>
      </div>
      {path.length > 0 && (
        <Breadcrumbs
          items={crumbs.map(({ href, label }) => ({ href, label }))}
          renderLink={({ href, label }) => (
            <Button
              tone="link"
              onClick={() => navigate(crumbs.find((c) => c.href === href)?.path ?? [])}
            >
              {label}
            </Button>
          )}
        />
      )}
      {/* A folder off the allowlist (a stray SFTP drop, a removed folder) refuses uploads. */}
      {uploadableSegments.includes(segment) &&
        (path.length === 0 || allowed.includes(path[0]!)) && (
          <UploadDropzone
            onFiles={(f) => void upload.uploadFiles(f)}
            uploading={upload.uploading}
            progress={upload.progress}
            error={upload.error}
          />
        )}
      <div
        ref={listingRef}
        role="region"
        tabIndex={-1}
        aria-label={`${crumbs[crumbs.length - 1]!.label} contents`}
      >
        <FileTable
          files={listing.files}
          folders={listing.folders}
          onOpenFolder={(name) => navigate([...path, name])}
          loading={loading}
          onDownload={(key) => void onDownload(key)}
        />
      </div>
      <Snackbar
        open={landed !== null}
        tone="success"
        message={
          landed && (
            <>
              Uploaded to{' '}
              {breadcrumbTrail(landed.segment, landed.path)
                .map((c) => c.label)
                .join(' / ')}
              .{' '}
              <Button
                tone="link"
                onClick={() => {
                  setSegment(landed.segment);
                  navigate(landed.path);
                }}
              >
                Open
              </Button>
            </>
          )
        }
        onClose={() => setLanded(null)}
      />
      <Snackbar
        open={error !== null}
        tone="danger"
        message={
          <>
            {error}{' '}
            <Button tone="link" onClick={() => void refresh()}>
              Retry
            </Button>
          </>
        }
        onClose={() => setError(null)}
      />
    </div>
  );
}

interface Landing {
  readonly segment: Segment;
  readonly path: FolderPath;
}

interface Crumb {
  readonly label: string;
  /** Unique per level, so a crumb resolves to its path even when labels repeat (month 11, day 11). */
  readonly href: string;
  readonly path: FolderPath;
}

function breadcrumbTrail(segment: Segment, path: FolderPath): Crumb[] {
  const root = segment === 'inbound' ? 'Inbound' : 'Outbound';
  return [
    { label: root, path: [] },
    ...path.map((label, i) => ({ label, path: path.slice(0, i + 1) })),
  ].map((c) => ({ ...c, href: `#${[segment, ...c.path].join('/')}` }));
}
